import { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_KEY;

if (!SUPABASE_URL || !SUPABASE_KEY) {
  throw new Error("Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_KEY — set them in .env.local or Vercel environment variables.");
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY, {
  auth: { flowType: 'implicit' },
});

// ─── Invite redirect (runs before React renders) ───────────────────────────────
// If the URL has ?token=HASH&invite=1 (our custom invite link format), redirect
// immediately through Supabase's verify endpoint. Supabase processes the token
// and sends the browser back to ?invite=1#access_token=...&type=invite.
// This avoids all PKCE/timing race conditions — session is in the hash on load.
;(function () {
  const sp = new URLSearchParams(window.location.search);
  const tok = sp.get("token");
  if (tok && sp.get("invite") === "1") {
    const redirectBack = `${window.location.origin}${window.location.pathname}?invite=1`;
    window.location.replace(
      `${SUPABASE_URL}/auth/v1/verify?token=${tok}&type=invite&redirect_to=${encodeURIComponent(redirectBack)}`
    );
  }
})();

// ─── Supabase-backed shared state hook ────────────────────────────────────────
export function useSharedState(column, defaultValue, roadmapId = "main", onSaveStart, onSaveEnd) {
  const defaultValueRef  = useRef(defaultValue);
  const [value, setValue] = useState(defaultValue);
  const [loaded, setLoaded] = useState(false);
  // Mirrors loadOkRef into render state so consumers can show a persistent
  // "not saving" warning. True only after a confirmed successful DB read.
  const [loadOk, setLoadOk] = useState(false);

  const skipNextSync     = useRef(false);
  const saveTimerRef     = useRef(null);
  const pendingValueRef  = useRef(null);
  const isSavePending    = useRef(false);
  const onSaveStartRef   = useRef(onSaveStart);
  const onSaveEndRef     = useRef(onSaveEnd);
  // CRITICAL data-safety guard: stays false until this roadmap/column has been
  // successfully read from the DB. While false, setAndSync refuses to write —
  // so a failed/slow initial load (which leaves `value` as the seed default)
  // can never overwrite real data in Supabase with seed/empty state.
  const loadOkRef        = useRef(false);
  // Tracks the updated_at of the last data we fetched or saved, so we can reject
  // realtime events that carry older snapshots (replayed events, cross-column echoes).
  const lastKnownAt      = useRef(null);
  // Tracks the last roadmapId+column we actually initialized for, so we can
  // distinguish a real roadmap switch (needs full reset) from a React Fast Refresh
  // effect re-run (same roadmap — skip the reset to avoid wiping visible data).
  const activeKeyRef     = useRef(null);

  // Keep callback refs current without rebuilding setAndSync
  useEffect(() => { onSaveStartRef.current = onSaveStart; });
  useEffect(() => { onSaveEndRef.current   = onSaveEnd;   });

  // Fetch on mount / roadmap switch — cancel if roadmap changes before it resolves
  useEffect(() => {
    const key = `${roadmapId}::${column}`;
    const isRoadmapSwitch = activeKeyRef.current !== key;
    activeKeyRef.current = key;

    // Only wipe local state when the roadmap actually changes (or on first mount).
    // During a Fast Refresh effect re-run the key is the same, so we skip the reset
    // and keep showing the current data while silently re-fetching in the background.
    if (isRoadmapSwitch) {
      setValue(defaultValueRef.current);
      setLoaded(false);
      setLoadOk(false);
      skipNextSync.current  = false;
      isSavePending.current = false;
      lastKnownAt.current   = null;
      loadOkRef.current     = false; // block writes until this roadmap loads successfully
    }

    let cancelled = false;

    // Load with retry. A transient failure (network/auth blip — common in the
    // seconds right after a redeploy) must NOT mark the column as loaded with
    // seed data showing, or the next save would overwrite real data. We retry
    // indefinitely with capped backoff so a blip self-heals and saves resume on
    // their own; loadOkRef only flips true on a genuine success.
    const load = (attempt) => {
      supabase
        .from("roadmap_state")
        .select(`${column}, updated_at`)
        .eq("id", roadmapId)
        .single()
        .then(({ data, error }) => {
          if (cancelled) return;
          if (!error && data) {
            if (data[column] != null)  setValue(data[column]);
            if (data.updated_at)       lastKnownAt.current = data.updated_at;
            loadOkRef.current = true;   // confirmed DB state — safe to persist now
            setLoadOk(true);            // clears any "not saving" warning in the UI
            setLoaded(true);
          } else {
            if (error) console.error(`Load error [${column}] (attempt ${attempt}):`, error);
            // Reveal the UI after the first burst so it isn't stuck on a spinner,
            // but keep loadOkRef false so saves stay blocked (and the warning
            // banner stays up) until a real load lands. Keep retrying forever
            // with backoff capped at 15s so the page recovers without a refresh.
            if (attempt >= 4) setLoaded(true);
            const delay = Math.min(1500 * attempt, 15000);
            setTimeout(() => { if (!cancelled) load(attempt + 1); }, delay);
          }
        });
    };
    load(1);

    return () => {
      cancelled = true;
      // Flush any in-flight debounced save before unmount or roadmap switch,
      // so data isn't lost when the dev server hot-reloads after a code change.
      // Guarded by loadOkRef: never flush if we never confirmed a load.
      if (saveTimerRef.current && pendingValueRef.current !== null && loadOkRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        supabase
          .from("roadmap_state")
          .update({ [column]: pendingValueRef.current, updated_at: new Date().toISOString() })
          .eq("id", roadmapId)
          .then(({ error }) => { if (error) console.error(`Flush save error [${column}]:`, error); });
      }
    };
  }, [column, roadmapId]);

  // Real-time subscription — only apply remote changes when no local write is pending
  useEffect(() => {
    const channel = supabase
      .channel(`roadmap_${roadmapId}_${column}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "roadmap_state", filter: `id=eq.${roadmapId}` },
        (payload) => {
          if (skipNextSync.current) return;
          if (payload.new[column] === undefined) return;
          // Reject events older than the last data we fetched or saved.
          // This blocks: (a) Supabase replaying old events on reconnect, and
          // (b) cross-column echoes where concurrent saves carry stale column values.
          const payloadTs = payload.new?.updated_at;
          if (payloadTs && lastKnownAt.current && payloadTs < lastKnownAt.current) return;
          if (payloadTs) lastKnownAt.current = payloadTs;
          setValue(payload.new[column]);
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [column, roadmapId]);

  const setAndSync = useCallback(
    (updater) => {
      // Capture the next value INSIDE the updater. React does NOT guarantee the
      // updater runs synchronously during this setValue() call — it only does so
      // via an eager-bailout optimization that it SKIPS whenever this hook already
      // has a pending update (e.g. a realtime echo's setValue just fired, or rapid
      // successive edits). When skipped, reading `next` after setValue returns
      // yields undefined, and the debounced PATCH below would send
      // `{[column]: undefined}` — which JSON.stringify drops, so PostgREST bumps
      // only updated_at and silently discards the change (other columns saved in
      // the same tick, like history, still persist — hence "the item just vanished
      // but the history entry stuck"). Assigning here makes the save deterministic.
      setValue((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        pendingValueRef.current = next;
        return next;
      });

      // Notify "saving" only on the first call in a debounce burst
      if (!isSavePending.current) {
        isSavePending.current = true;
        onSaveStartRef.current?.();
      }

      // Debounce: reset the timer on every rapid call, always save the latest value
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      skipNextSync.current = true;

      saveTimerRef.current = setTimeout(() => {
        saveTimerRef.current = null; // clear so unmount flush doesn't re-fire after a completed save
        // CRITICAL: never write before a confirmed successful load. If the initial
        // fetch failed/stalled, `value` is still the seed default — persisting it
        // here would overwrite the real roadmap in the DB. Skip and report an error
        // so the save indicator shows the write didn't happen.
        if (!loadOkRef.current) {
          isSavePending.current = false;
          onSaveEndRef.current?.(new Error("Save blocked — roadmap data not loaded yet"));
          return;
        }
        const valueToSave = pendingValueRef.current;
        // Safety net for the undefined-drop class of bug: never PATCH an
        // undefined column value. JSON.stringify silently omits undefined keys,
        // which would bump updated_at while discarding the change with no error.
        // Surface it loudly instead of losing data quietly.
        if (valueToSave === undefined) {
          isSavePending.current = false;
          onSaveEndRef.current?.(new Error(`Save aborted — no pending value for ${column}`));
          return;
        }
        const savedAt = new Date().toISOString();
        supabase
          .from("roadmap_state")
          .update({ [column]: valueToSave, updated_at: savedAt })
          .eq("id", roadmapId)
          .then(({ error }) => {
            isSavePending.current = false;
            if (!error) lastKnownAt.current = savedAt;
            if (error) console.error(`Save error [${column}]:`, error);
            onSaveEndRef.current?.(error ?? null);
            // Hold skipNextSync true for 2s after save to block cross-column realtime echoes.
            // When two columns save concurrently each realtime event carries the pre-save
            // snapshot of the other columns — without this guard those stale values overwrite
            // local state and the next user action saves them back to DB, losing data.
            setTimeout(() => { skipNextSync.current = false; }, 2000);
          });
      }, 300);
    },
    [column, roadmapId]
  );

  return [value, setAndSync, loaded, loadOk];
}

// ─── Roadmap list hook ─────────────────────────────────────────────────────────
// Names are stored inside the settings JSON column (settings.roadmapName)
// so no extra columns are needed in roadmap_state.
export function useRoadmapList() {
  const [roadmaps, setRoadmaps] = useState([]);
  const [loaded,   setLoaded]   = useState(false);

  const refetch = useCallback(async () => {
    const { data, error } = await supabase
      .from("roadmap_state")
      .select("id, settings, updated_at")
      .neq("id", "_global")
      .order("updated_at", { ascending: false });
    if (!error && data) {
      setRoadmaps(data.map(r => ({
        id: r.id,
        name: r.settings?.roadmapName
           || (r.id === "main" ? "Main Roadmap" : "Untitled Roadmap"),
        updatedAt: r.updated_at,
      })));
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    refetch();
    const channel = supabase
      .channel("roadmap_list_meta")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "roadmap_state" }, refetch)
      .on("postgres_changes", { event: "DELETE", schema: "public", table: "roadmap_state" }, refetch)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [refetch]);

  return [roadmaps, loaded, refetch];
}

// ─── Roadmap CRUD ─────────────────────────────────────────────────────────────
export async function createRoadmap(name, initialLanes = null) {
  const id = `rm_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const row = {
    id,
    settings: { sprintYear: new Date().getFullYear(), roadmapName: name || "New Roadmap" },
    projects: [],
    backlog: [],
    completed: [],
    release_schedule: { events: [], codeFreezes: [], notes: [] },
    history: [],
    updated_at: new Date().toISOString(),
  };
  if (initialLanes) row.lanes = initialLanes;
  const { error } = await supabase.from("roadmap_state").insert(row);
  if (error) throw error;
  return id;
}

export async function deleteRoadmap(id) {
  if (id === "main") throw new Error("Cannot delete the main roadmap");
  const { error } = await supabase.from("roadmap_state").delete().eq("id", id);
  if (error) throw error;
}

export async function renameRoadmap(id, name) {
  if (!name?.trim()) throw new Error("Name is required");
  // Fetch current settings so we can merge — avoids clobbering sprintYear etc.
  const { data, error: fetchErr } = await supabase
    .from("roadmap_state").select("settings").eq("id", id).single();
  if (fetchErr) throw fetchErr;
  const { error } = await supabase
    .from("roadmap_state")
    .update({
      settings: { ...(data.settings || {}), roadmapName: name.trim() },
      updated_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

// ─── Global settings (_global row) ───────────────────────────────────────────
export async function fetchGlobalSettings() {
  const { data } = await supabase
    .from("roadmap_state")
    .select("settings")
    .eq("id", "_global")
    .maybeSingle();
  return {
    publicMode: data?.settings?.publicMode === true,
    anonIdeas:  data?.settings?.anonIdeas  === true,
  };
}

export async function updateGlobalSettings(patch) {
  const { data } = await supabase
    .from("roadmap_state")
    .select("settings")
    .eq("id", "_global")
    .maybeSingle();
  const merged = { ...(data?.settings || {}), ...patch };
  const { error } = await supabase
    .from("roadmap_state")
    .upsert({
      id: "_global",
      settings: merged,
      projects: [], backlog: [], completed: [], history: [],
      updated_at: new Date().toISOString(),
    }, { onConflict: "id" });
  if (error) throw error;
}

// ─── Supabase Auth hook ────────────────────────────────────────────────────────
export function useAuth() {
  const [session, setSession] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const [needsPasswordSet, setNeedsPasswordSet] = useState(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    return hash.includes("type=invite") || hash.includes("type=recovery") || search.includes("invite=1");
  });

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const isInviteFlow = hash.includes("type=invite") || search.includes("invite=1");

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === "PASSWORD_RECOVERY") setNeedsPasswordSet(true);
      if (event === "SIGNED_IN" && isInviteFlow) setNeedsPasswordSet(true);
    });

    if (isInviteFlow && hash.includes("access_token=")) {
      // Supabase isn't processing the hash automatically — do it manually
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get("access_token");
      const refreshToken = params.get("refresh_token");

      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ data }) => {
          if (data?.session) {
            setSession(data.session);
            setNeedsPasswordSet(true);
          }
          setAuthLoaded(true);
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
        });
    } else {
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSession(session);
        setAuthLoaded(true);
      });
    }

    return () => subscription.unsubscribe();
  }, []);

  return { session, authLoaded, needsPasswordSet, setNeedsPasswordSet };
}
